import { GATE_STATUS_LABELS, STAGES, type GateStatus, type StageGate } from '@ave/shared';
import { Icon } from './icons';

const DOT: Record<GateStatus, string> = {
  passed: 'bg-teal-600 text-white ring-teal-600',
  in_progress: 'bg-white text-teal-700 ring-teal-500',
  awaiting_approval: 'bg-white text-teal-700 ring-teal-500',
  blocked: 'bg-white text-amber-700 ring-amber-500',
  rejected: 'bg-white text-rose-700 ring-rose-500',
  not_started: 'bg-white text-slate-400 ring-slate-200',
};

/**
 * The eleven stages, always visible.
 *
 * The rail is the product's spine: a valuer should be able to tell at a
 * glance which gate they are standing at, what is behind them, and where
 * the engagement is blocked — without opening anything.
 */
export function StageRail({
  gates,
  currentStage,
  selected,
  onSelect,
}: {
  gates: StageGate[];
  currentStage: number;
  selected: number;
  onSelect: (stage: number) => void;
}) {
  const byStage = new Map(gates.map((g) => [g.stage, g]));

  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex min-w-[900px] items-start">
        {STAGES.map((def, i) => {
          const gate = byStage.get(def.stage);
          const status: GateStatus = gate?.status ?? 'not_started';
          const isSelected = selected === def.stage;
          const isCurrent = currentStage === def.stage;
          const blockingCount =
            gate?.validations.filter((v) => v.severity === 'blocking' && !v.passed).length ?? 0;

          return (
            <div key={def.stage} className="relative flex flex-1 flex-col items-center">
              {i > 0 ? (
                <span
                  className={`absolute left-[-50%] top-[15px] h-0.5 w-full ${
                    status === 'passed' || (byStage.get(def.stage - 1)?.status === 'passed')
                      ? 'bg-teal-500'
                      : 'bg-slate-200'
                  }`}
                  aria-hidden
                />
              ) : null}

              <button
                type="button"
                onClick={() => onSelect(def.stage)}
                aria-current={isCurrent ? 'step' : undefined}
                className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ring-2 transition-all ${DOT[status]} ${
                  isSelected ? 'scale-110 shadow-md' : 'hover:scale-105'
                }`}
                title={`${def.stage}. ${def.name} — ${GATE_STATUS_LABELS[status]}`}
              >
                {status === 'passed' ? (
                  <Icon name="check" className="h-4 w-4" strokeWidth={2.5} />
                ) : status === 'blocked' ? (
                  <Icon name="alert" className="h-4 w-4" strokeWidth={2} />
                ) : (
                  def.stage
                )}
                {blockingCount > 0 && status !== 'passed' ? (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-white ring-2 ring-white">
                    {blockingCount}
                  </span>
                ) : null}
              </button>

              <span
                className={`mt-2 px-1 text-center text-[11px] leading-tight ${
                  isSelected ? 'font-semibold text-slate-900' : 'text-slate-500'
                }`}
              >
                {def.name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
